// Global Instructions Rule Applied!
//
// My Agents — the personal agent screen (Fund Management → MY WORKSPACE,
// /my-agents). Two AntD tabs:
//   * Agent activity  — DEFAULT and first (owner 2026-09-02). The
//     propose-approve review surface (design §6): agent-filed proposals await
//     the user's approve/reject; approve replays the exact payload through the
//     real write path as the user. It leads because it is the tab people come
//     back to — the grants list is visited only when something needs changing.
//     It lives here (not on the deal pipeline) because a proposal is an
//     agent-delegation artefact until approved — the deal appears in the
//     pipeline only after approval, via the normal path.
//   * Connected agents — self-service grant lifecycle (Phase 3).
//
// The whole screen is gated per user by cfg: the menu item carries
// visibility_rule 'has_connected_agent', so someone who has never connected an
// agent never sees it. Connecting happens on its own screen
// (Configuration -> Connect an Agent) because it is the one part of this flow a
// user needs BEFORE they have a grant, and therefore before this item exists.
//
// The former admin "Agent products" tab is gone (owner 2026-09-02): it
// duplicated Configuration -> Agent Gateway -> Products verbatim, down to the
// approve/disable Popconfirm pair, and product approval is an administrator's
// job in an administrator's area.
//
// Both tabs are owner-scoped by RLS (agent_grants, agent_proposals).
//
// All panels are self-contained (own hook, fetch, loading) — no Redux slice.

import React, { useMemo, useState } from 'react'
import { Tabs, Typography } from 'antd'
import { ApiOutlined } from '@ant-design/icons'
import { MyAgentsTab } from './components/MyAgentsTab.tsx'
import { ProposalsTab } from './components/ProposalsTab.tsx'
import { HelpButton } from '../../core/components/HelpDrawer/HelpButton.tsx'
import { HELP_ARTICLES } from '../../ui_configs/helpArticles.ts'

const { Title } = Typography

// Per-tab user guides (cfg.help_articles.article_key). A tab with no entry
// simply shows no "?" — the button hides itself rather than opening an empty
// drawer, so tabs can be given guides one at a time.
const TAB_HELP_ARTICLES: Record<string, string> = {
  'my-agents': HELP_ARTICLES.connectedAgents
}

export default function AgentAccessPage() {
  // Agent activity opens first (owner 2026-09-02). The tab KEY stays
  // 'proposals' — it is the surface's name throughout the code and the key in
  // TAB_HELP_ARTICLES — while the LABEL is what the user reads.
  const [activeKey, setActiveKey] = useState<string>('proposals')

  const tabItems = useMemo(
    () => [
      { key: 'proposals', label: 'Agent activity', children: <ProposalsTab /> },
      { key: 'my-agents', label: 'Connected agents', children: <MyAgentsTab /> }
    ],
    []
  )

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white px-4 py-3 overflow-hidden'>
        <div className='flex items-center gap-2' style={{ margin: '0 0 8px 0' }}>
          <Title level={4} style={{ margin: 0 }}>
            <ApiOutlined style={{ marginRight: 8 }} />
            My Agents
          </Title>
          {/* Page-level help: the guide belongs to the screen, not to one
              section of it, and the drawer is non-modal so the tab below
              stays usable while it is open. */}
          <HelpButton articleKey={TAB_HELP_ARTICLES[activeKey]} variant='title' />
        </div>
        <Tabs
          activeKey={activeKey}
          onChange={setActiveKey}
          destroyInactiveTabPane={false}
          items={tabItems}
          className='flex-1 min-h-0'
        />
      </div>
    </div>
  )
}
